import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useSIEMAlerts, createAlert } from '../../hooks/useSecOps';
import { useSignalRRefresh } from '../../hooks/useSignalR';
import { EmptyState } from '../../components/EmptyState';

interface Alert {
  id: string;
  title: string;
  severity: string;
  status: string;
  source: string;
  category: string;
  assigned_to: string;
  detected_at: string;
  mitre_techniques: string[];
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
  info: 'bg-gray-100 text-gray-600',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  open: 'bg-red-100 text-red-700',
  investigating: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
  false_positive: 'bg-gray-100 text-gray-500',
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default function AlertsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [newAlert, setNewAlert] = useState({ title: '', severity: 'medium', category: '', source: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [sortField, setSortField] = useState<string>('detected_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, loading, error, refresh } = useSIEMAlerts(filters);
  useSignalRRefresh('siemAlert', refresh);
  const alerts = ((data?.alerts ?? []) as Alert[]);

  const sorted = [...alerts].sort((a, b) => {
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    if (sortField === 'severity') {
      const diff = (severityOrder[a.severity as keyof typeof severityOrder] ?? 5) - (severityOrder[b.severity as keyof typeof severityOrder] ?? 5);
      return sortDir === 'asc' ? diff : -diff;
    }
    const aVal = String((a as unknown as Record<string, unknown>)[sortField] ?? '');
    const bVal = String((b as unknown as Record<string, unknown>)[sortField] ?? '');
    return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  function toggleSort(field: string) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  }

  function setFilter(key: string, value: string) {
    setFilters(f => value ? { ...f, [key]: value } : Object.fromEntries(Object.entries(f).filter(([k]) => k !== key)));
  }

  async function handleCreate() {
    if (!newAlert.title) return;
    setCreating(true);
    try {
      await createAlert(newAlert);
      setShowCreate(false);
      setNewAlert({ title: '', severity: 'medium', category: '', source: '', description: '' });
      refresh();
    } catch { /* handled */ } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">SIEM Alerts</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{data?.total ?? 0} total</span>
          <button onClick={() => setShowCreate(true)} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            Create Alert
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center justify-between">{error} <button onClick={refresh} className="text-sm font-medium underline">Retry</button></div>}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-3 items-center">
        <select value={filters.severity ?? ''} onChange={e => setFilter('severity', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
          <option value="">All Severities</option>
          {['critical', 'high', 'medium', 'low', 'info'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>

        <select value={filters.status ?? ''} onChange={e => setFilter('status', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
          <option value="">All Statuses</option>
          {['new', 'open', 'investigating', 'resolved', 'closed', 'false_positive'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</option>)}
        </select>

        <input type="text" placeholder="Category..." value={filters.category ?? ''} onChange={e => setFilter('category', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-36" />

        <input type="date" value={filters.from_date ?? ''} onChange={e => setFilter('from_date', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
        <input type="date" value={filters.to_date ?? ''} onChange={e => setFilter('to_date', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />

        {Object.keys(filters).length > 0 && (
          <button onClick={() => setFilters({})} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Clear filters</button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th onClick={() => toggleSort('title')} className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3 cursor-pointer hover:text-gray-700">Title {sortField === 'title' && (sortDir === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => toggleSort('severity')} className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3 cursor-pointer hover:text-gray-700">Severity {sortField === 'severity' && (sortDir === 'asc' ? '▲' : '▼')}</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Source</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Category</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Assigned To</th>
              <th onClick={() => toggleSort('detected_at')} className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3 cursor-pointer hover:text-gray-700">Detected {sortField === 'detected_at' && (sortDir === 'asc' ? '▲' : '▼')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={<Bell className="w-12 h-12" />}
                    title="No alerts found"
                    description="No SIEM alerts match the current filters."
                  />
                </td>
              </tr>
            )}
            {sorted.map(alert => (
              <tr key={alert.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/secops/alerts/${alert.id}`)}>
                <td className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{alert.title}</p>
                </td>
                <td className="px-4 py-4"><Badge value={alert.severity} colors={SEVERITY_COLORS} /></td>
                <td className="px-4 py-4"><Badge value={alert.status} colors={STATUS_COLORS} /></td>
                <td className="px-4 py-4 text-sm text-gray-700">{alert.source || '-'}</td>
                <td className="px-4 py-4 text-sm text-gray-700">{alert.category || '-'}</td>
                <td className="px-4 py-4 text-sm text-gray-700">{alert.assigned_to || 'Unassigned'}</td>
                <td className="px-4 py-4 text-xs text-gray-500">{alert.detected_at ? new Date(alert.detected_at).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Alert</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={newAlert.title} onChange={e => setNewAlert(a => ({ ...a, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Alert title..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                  <select value={newAlert.severity} onChange={e => setNewAlert(a => ({ ...a, severity: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    {['critical', 'high', 'medium', 'low', 'info'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <input type="text" value={newAlert.source} onChange={e => setNewAlert(a => ({ ...a, source: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="EDR, SIEM..." />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input type="text" value={newAlert.category} onChange={e => setNewAlert(a => ({ ...a, category: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="malware, phishing, intrusion..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={newAlert.description} onChange={e => setNewAlert(a => ({ ...a, description: e.target.value }))}
                  rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Describe the alert..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !newAlert.title}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Alert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
